import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, finalize } from 'rxjs/operators';
import { ApiResponse, SupplyOrder, OrderDocument } from '@app/types';

@Injectable({
    providedIn: 'root',
})
export class OrdersService {
    private readonly http = inject(HttpClient);

    private _supplyOrders: WritableSignal<SupplyOrder[]> = signal([]);
    public readonly supplyOrdersSignal = this._supplyOrders.asReadonly();

    private _isLoading: WritableSignal<boolean> = signal(true);
    public readonly isLoadingSignal = this._isLoading.asReadonly();

    private readonly API_URL = environment.API_URL;

    constructor() {
        // No initial load
    }

    // Always fetch fresh from API - no cache
    loadSupplyOrders(forceRefresh: boolean = true): Observable<SupplyOrder[]> {
        this._isLoading.set(true);

        const body = { filters: {}, limit: 5000, offset: 0, include_relations: true };
        return this.http.post<ApiResponse<SupplyOrder>>(`${this.API_URL}/orders/read`, body).pipe(
            map((response) => {
                // Deep clone to prevent shared references
                const ordersConFechas = response.data.map((o: any) => {
                    // Create completely new object for each order
                    const order: any = {
                        id: o.id,
                        folioOrden: o.folioOrden,
                        clienteRFC: o.clienteRFC,
                        clienteNombre: o.clienteNombre,
                        pedido: o.pedido,
                        folioFactura: o.folioFactura,
                        folioRemisionCliente: o.folioRemisionCliente,
                        folioRemisionInterno: o.folioRemisionInterno,
                        facturaId: o.facturaId,
                        deleted: o.deleted,
                        docs: o.docs ? [...o.docs] : [],
                        fechaCreacion: o.fechaCreacion ? new Date(o.fechaCreacion) : new Date(),
                        fechaUltimoCambio: o.fechaUltimoCambio ? new Date(o.fechaUltimoCambio) : new Date()
                    };

                    // Deep clone remision
                    if (o.remision) {
                        order.remision = {
                            folio: o.remision.folio,
                            estatus: o.remision.estatus,
                            fechaCambioEstatus: o.remision.fechaCambioEstatus ? new Date(o.remision.fechaCambioEstatus) : new Date()
                        };
                    }

                    // Deep clone factura - THIS IS CRITICAL
                    if (o.factura && o.factura.id) {
                        order.factura = {
                            id: o.factura.id,
                            uuid: o.factura.uuid,
                            serie: o.factura.serie,
                            folio: o.factura.folio,
                            fechaEmision: o.factura.fechaEmision ? new Date(o.factura.fechaEmision) : new Date(),
                            fechaTimbrado: o.factura.fechaTimbrado ? new Date(o.factura.fechaTimbrado) : new Date(),
                            receptorNombre: o.factura.receptorNombre,
                            receptorRFC: o.factura.receptorRFC,
                            emisorNombre: o.factura.emisorNombre,
                            emisorRFC: o.factura.emisorRFC,
                            total: o.factura.total,
                            subtotal: o.factura.subtotal,
                            iva: o.factura.iva,
                            moneda: o.factura.moneda,
                            estatus: o.factura.estatus,
                            seleccionada: false,
                            statusHistory: o.factura.statusHistory ? o.factura.statusHistory.map((h: any) => ({
                                estatus: h.estatus,
                                fecha: h.fecha ? new Date(h.fecha) : new Date(),
                                observacion: h.observacion,
                                userId: h.userId
                            })) : [],
                            ordenSurtimientoId: o.factura.ordenSurtimientoId
                        };
                    } else {
                        order.factura = null;
                    }

                    return order as SupplyOrder;
                });

                this._supplyOrders.set(ordersConFechas);
                return ordersConFechas;
            }),
            catchError((err) => {
                console.error('❌ Error fetching orders from API', err);
                return throwError(() => err);
            }),
            finalize(() => this._isLoading.set(false))
        );
    }

    // Force reload after mutation and emit fresh list
    updateOrderDocuments(orderId: string, documents: OrderDocument[]): Observable<ApiResponse<SupplyOrder>> {
        const updatePayload = [{ id: orderId, changes: { docs: documents } }];
        return this.http.patch<ApiResponse<SupplyOrder>>(`${this.API_URL}/orders`, updatePayload).pipe(
            switchMap((response) => {
                if (response.state === 'success') {
                    return this.loadSupplyOrders(true).pipe(map(() => response));
                }
                return of(response);
            }),
            catchError((err) => {
                console.error('Error updating order documents', err);
                return throwError(() => err);
            }),
        );
    }

    // Force reload after mutation and emit fresh list
    validateRemisionInApi(orderId: string): Observable<ApiResponse<SupplyOrder>> {
        const updatePayload = [{ id: orderId, changes: { remision: { estatus: 'Validada' } } }];
        return this.http.patch<ApiResponse<SupplyOrder>>(`${this.API_URL}/orders`, updatePayload).pipe(
            switchMap((response) => {
                if (response.state === 'success') {
                    return this.loadSupplyOrders(true).pipe(map(() => response));
                }
                return of(response);
            }),
            catchError((err) => {
                console.error('Error validating remision', err);
                return throwError(() => err);
            }),
        );
    }
}
